/* eslint-disable react/jsx-max-depth */
import React, { useEffect, useState } from 'react';

import {
  Button,
  Grid,
  GridItem,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { type OptionBase } from 'chakra-react-select';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField, Select, SlideIn } from '@/components';
import {
  useCreateInterviewSkillMutation,
  useGetInterviewSkillsQuery,
} from '@/features/admin-new-interview';
import { QuestionType } from '@/services/api';

import {
  DEFAULT_THINKING_TIME,
  QUESTION_WEIGHTS,
  THINKING_TIMES,
} from './constants';
import { useGenerateAndGetInterviewQuestionTagsMutation } from './queries';
import {
  type FormFieldsInterviewQuestionSettings,
  zFormFieldsInterviewQuestionSettings,
} from './schemas';

type SettingsDTO = {
  skill?: string;
  tags?: Array<string>;
  thinkingTime: number;
  questionWeight?: number;
  answerAgain?: boolean;
  ignoreAiRating?: boolean;
  isAdminQuestion?: boolean;
  isEmotionAnalysisEnabled?: boolean;
};

type QuestionSettingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialValues: Partial<SettingsDTO>;
  onSubmit: (newData: SettingsDTO) => void;
  questionType: QuestionType | null;
  questionText: string;
};

interface ThinkingTimeOptions extends OptionBase {
  label: string;
  value: (typeof THINKING_TIMES)[number];
}

interface QuestionWeightOptions extends OptionBase {
  label: (typeof QUESTION_WEIGHTS)[number];
  value: (typeof QUESTION_WEIGHTS)[number];
}

interface SkillOptions extends OptionBase {
  label: string;
  value: string;
}

export interface TagOptions extends OptionBase {
  label: string;
  value: string;
}

const toSkillOptions = (skill: string): SkillOptions => {
  return {
    label: skill,
    value: skill,
  };
};

export const toTagOptions = (tag: string): TagOptions => {
  return {
    label: tag,
    value: tag,
  };
};

export const QuestionSettingModal: React.FC<QuestionSettingModalProps> = ({
  isOpen,
  onClose,
  questionType,
  initialValues,
  onSubmit: onUpdate,
  questionText,
}) => {
  const { t } = useTranslation(['interview', 'common']);
  const [aiGeneratedTags, setAiGeneratedTags] = useState<Array<string>>([]);

  const { data: skillOptions } = useGetInterviewSkillsQuery();

  const toThinkingTimeOptions = (
    thinkingTime: (typeof THINKING_TIMES)[number]
  ): ThinkingTimeOptions => {
    const timeValue = Number(thinkingTime);

    if (timeValue >= 60) {
      const minutes = timeValue / 60;
      return {
        label:
          minutes === 1
            ? t('common:timeAndDate.minute_one')
            : t('common:timeAndDate.minute_other', { count: minutes }),
        value: thinkingTime,
      };
    }

    return {
      label: thinkingTime + ' ' + t('common:timeAndDate.seconds'),
      value: thinkingTime,
    };
  };

  const toQuestionWeightOptions = (
    questionWeight: (typeof QUESTION_WEIGHTS)[number]
  ): QuestionWeightOptions => {
    return {
      label: questionWeight,
      value: questionWeight,
    };
  };

  const { mutateAsync: addSkill, isPending } =
    useCreateInterviewSkillMutation();

  const form = useForm<FormFieldsInterviewQuestionSettings>({
    mode: 'onSubmit',
    resolver: zodResolver(zFormFieldsInterviewQuestionSettings()),
    defaultValues: {
      thinkingTime: DEFAULT_THINKING_TIME,
    },
    values: {
      thinkingTime: initialValues.thinkingTime
        ? (initialValues.thinkingTime.toString() as unknown as (typeof THINKING_TIMES)[number])
        : DEFAULT_THINKING_TIME,
      answerAgain: initialValues.answerAgain,
      ignoreRating: initialValues.ignoreAiRating,
      questionWeight: initialValues.questionWeight
        ? (initialValues.questionWeight.toString() as unknown as (typeof QUESTION_WEIGHTS)[number])
        : undefined,
      skillTitle: initialValues.skill,
      tags: initialValues.tags,
      isAdminQuestion: initialValues.isAdminQuestion,
    },
  });

  const { control, handleSubmit } = form;

  const handleAddNewSkill = async (newSkill: string) => {
    await addSkill({ skill: newSkill });
  };

  const generateAndGetInterviewQuestionTagsMutation =
    useGenerateAndGetInterviewQuestionTagsMutation();

  const onFormSubmit: SubmitHandler<
    FormFieldsInterviewQuestionSettings
  > = async (data, ev) => {
    ev?.stopPropagation();

    const d: SettingsDTO = {
      skill: data.skillTitle,
      thinkingTime: data.thinkingTime
        ? Number(data.thinkingTime)
        : Number(DEFAULT_THINKING_TIME),
      questionWeight: data.questionWeight
        ? Number(data.questionWeight)
        : undefined,
      isAdminQuestion: data.isAdminQuestion ?? false,
      answerAgain: data.answerAgain ?? false,
      ignoreAiRating: data.ignoreRating ?? false,
      tags: data.tags,
    };

    onUpdate(d);

    onClose();
  };

  const fetchTags = async () => {
    const resp = await generateAndGetInterviewQuestionTagsMutation.mutateAsync({
      question: questionText,
      previousTags: initialValues.tags,
    });

    setAiGeneratedTags(resp);
  };

  useEffect(() => {
    if (isOpen && questionText) {
      fetchTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialValues.tags, questionText]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent borderRadius="xl" pt={6} alignItems="center">
        <ModalCloseButton
          borderRadius="full"
          borderWidth="1px"
          borderColor="gray.100"
        />

        <VStack gap={0} w="100%" px={6}>
          <ModalHeader justifyItems="center" px={0}>
            <Text textAlign="center" fontSize="xl" fontWeight="semibold">
              {t('interview:createNewInterview.questionPreparation.settings')}
            </Text>
          </ModalHeader>

          <ModalBody w="full" pb={2} px={0}>
            <SlideIn>
              <Form noHtmlForm {...form}>
                <Stack spacing={4}>
                  <FormField
                    placeholder={t(
                      'interview:createNewInterview.questionPreparation.settingsSkillTitle'
                    )}
                    label={t(
                      'interview:createNewInterview.questionPreparation.settingsSkillTitle'
                    )}
                    type="creatable-select"
                    control={control}
                    name="skillTitle"
                    options={skillOptions?.map(toSkillOptions) ?? []}
                    onCreateOption={(createdSkill) => {
                      if (!createdSkill) return;

                      // Note: When using 'onCreateOption' prop, onChange is not called for manually created options.
                      handleAddNewSkill(createdSkill);
                      form.setValue('skillTitle', createdSkill);
                    }}
                    isLoading={isPending}
                  />

                  <FormField
                    type="custom"
                    control={control}
                    name="tags"
                    render={({ field }) => {
                      const { value, onChange } = field;
                      const currentTags = value ?? [];

                      const handleTagChange = (newValue: ExplicitAny) => {
                        onChange(
                          newValue.map((item: TagOptions) => item.value)
                        );
                      };

                      const handleCreateTag = (newTag: string) => {
                        // Ensure there are no more than 30 tags
                        if (currentTags.length < 30) {
                          const newOption: string = newTag;
                          onChange([...currentTags, newOption]);
                        }
                      };

                      return (
                        <Select
                          type="creatable"
                          tagVariant="outline"
                          value={currentTags.map(toTagOptions)}
                          isMulti
                          onChange={handleTagChange}
                          onCreateOption={handleCreateTag}
                          options={
                            (aiGeneratedTags?.map(
                              toTagOptions
                            ) as ExplicitAny) ?? []
                          }
                          placeholder={t(
                            'interview:createNewInterview.questionPreparation.addTag'
                          )}
                          isLoading={
                            generateAndGetInterviewQuestionTagsMutation.isPending
                          }
                        />
                      );
                    }}
                  />

                  <Grid columnGap={3} templateColumns="repeat(2, 1fr)">
                    <GridItem colSpan={1}>
                      <FormField
                        placeholder={t(
                          'createNewInterview.questionPreparation.thinkingTime'
                        )}
                        label={t(
                          'createNewInterview.questionPreparation.thinkingTime'
                        )}
                        type="select"
                        control={control}
                        name="thinkingTime"
                        options={THINKING_TIMES.map(toThinkingTimeOptions)}
                      />
                    </GridItem>

                    <GridItem colSpan={1}>
                      <FormField
                        placeholder={t(
                          'createNewInterview.questionPreparation.questionWeight'
                        )}
                        label={t(
                          'createNewInterview.questionPreparation.questionWeight'
                        )}
                        type="select"
                        control={control}
                        name="questionWeight"
                        size="lg"
                        options={QUESTION_WEIGHTS.map(toQuestionWeightOptions)}
                      />
                    </GridItem>
                  </Grid>

                  <FormField
                    type="custom"
                    control={control}
                    name="answerAgain"
                    render={({ field: { value, ...rest } }) => {
                      return (
                        <HStack>
                          <Switch
                            checked={value}
                            defaultChecked={value}
                            colorScheme="brand"
                            color="red.500"
                            size="md"
                            {...rest}
                          />
                          <Text fontSize={14}>
                            {t(
                              'interview:createNewInterview.questionPreparation.answerAgain'
                            )}
                          </Text>
                        </HStack>
                      );
                    }}
                  />

                  <FormField
                    type="custom"
                    control={control}
                    name="ignoreRating"
                    render={({ field: { value, ...rest } }) => {
                      return (
                        <HStack>
                          <Switch
                            disabled={questionType === QuestionType.File}
                            checked={value}
                            defaultChecked={value}
                            colorScheme="brand"
                            color="red.500"
                            size="md"
                            {...rest}
                          />
                          <Text fontSize={14}>
                            {t(
                              'interview:createNewInterview.questionPreparation.settingsAIScoringIgnorance'
                            )}
                          </Text>
                        </HStack>
                      );
                    }}
                  />

                  {[
                    QuestionType.OpenEnded,
                    QuestionType.MultipleChoice,
                    QuestionType.MultipleSelection,
                    QuestionType.File,
                  ].includes(questionType as QuestionType) && (
                    <FormField
                      type="custom"
                      control={control}
                      name="isAdminQuestion"
                      render={({ field: { value, ...rest } }) => {
                        return (
                          <HStack>
                            <Switch
                              isChecked={value}
                              defaultChecked={value}
                              colorScheme="brand"
                              color="red.500"
                              size="md"
                              {...rest}
                            />
                            <Text fontSize={14}>
                              {t(
                                'interview:createNewInterview.questionPreparation.settingsAdminEvaluation'
                              )}
                            </Text>
                          </HStack>
                        );
                      }}
                    />
                  )}

                  <Button
                    mb={4}
                    variant="@primary"
                    w="full"
                    onClick={() => {
                      // if (!form.getValues('skillTitle')) {
                      //   form.setError('skillTitle', {
                      //     message: t(
                      //       'interview:createNewInterview.questionPreparation.skillTitleCanNotBeEmpty'
                      //     ),
                      //     type: 'required',
                      //   });
                      //   return;
                      // }

                      handleSubmit(onFormSubmit)();
                    }}
                    isLoading={isPending}
                  >
                    {t(
                      'interview:createNewInterview.questionPreparation.saveSettings'
                    )}
                  </Button>
                </Stack>
              </Form>
            </SlideIn>
          </ModalBody>
        </VStack>
      </ModalContent>
    </Modal>
  );
};
